import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import LoadingSpinner from '../components/LoadingSpinner';
import usePageTitle from '@/hooks/usePageTitle';

// SVG components for the buttons
const GoogleLogo: React.FC = () => (
  <svg width="18" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const AppleLogo: React.FC = () => (
  <svg width="16" height="18" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
    <path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z" />
  </svg>
);

const Login: React.FC = () => {
  const { signInWithApple, signInWithEmail, signInWithGoogle, user } = useAuth();
  const { theme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();

  const query = new URLSearchParams(location.search);
  const emailFromQuery = query.get('email') || '';

  const [email, setEmail] = useState(emailFromQuery);
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const passwordRef = useRef<HTMLInputElement>(null);

  // Get the redirect path from location state
  const from = location.state?.from?.pathname || '/dashboard';

  // Set page title
  usePageTitle('Sign In');

  // Get email from location state if available
  useEffect(() => {
    if (location.state && location.state.email) {
      setEmail(location.state.email);
    }
  }, [location.state]);

  useEffect(() => {
    if (emailFromQuery && passwordRef.current) {
      passwordRef.current.focus();
    }
  }, [emailFromQuery]);

  useEffect(() => {
    // Only redirect if authentication is not loading, we have a user, and no redirect is in progress
    if (user && !localStorage.getItem('authRedirectState')) {
      console.log('User is authenticated on login page, redirecting to:', from);
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError('');
      setLocalLoading(true);
      await signInWithEmail(email, password);
      // Navigation will be handled by the useEffect above
    } catch (err) {
      setError('Failed to sign in. Please check your credentials.');
      console.error('Login error:', err);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleGoogleSignIn = async (e: React.MouseEvent) => {
    e.preventDefault();
    console.log('Google sign-in button clicked');
    console.log('Redirect path:', from);

    if (localLoading) {
      console.log('Already processing sign-in, ignoring click');
      return;
    }

    try {
      setError('');
      setLocalLoading(true);
      console.log('Initiating Google sign-in with redirect path:', from);
      await signInWithGoogle(from);
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError('Failed to start Google sign in. Please try again.');
    } finally {
      // Always reset loading state after the sign-in attempt
      setLocalLoading(false);
    }
  };

  const handleAppleSignIn = async (e: React.MouseEvent) => {
    e.preventDefault();

    if (localLoading) return;

    try {
      setError('');
      setLocalLoading(true);
      await signInWithApple(from);
    } catch (err) {
      console.error('Apple sign-in error:', err);
      setError('Failed to start Apple sign in. Please try again.');
    } finally {
      // Always reset loading state after the sign-in attempt
      setLocalLoading(false);
    }
  };

  // Check if we're in a redirect or local loading state
  const redirectState = localStorage.getItem('authRedirectState');
  let isRedirecting = false;

  if (redirectState) {
    try {
      const state = JSON.parse(redirectState);
      // Only consider it a valid redirect if it's less than 30 seconds old
      if (state.timestamp && state.timestamp > Date.now() - 30000) {
        isRedirecting = true;
      } else {
        // Clean up stale redirect state
        localStorage.removeItem('authRedirectState');
      }
    } catch (e) {
      // Invalid redirect state, clean it up
      localStorage.removeItem('authRedirectState');
    }
  }

  const isLoading = localLoading || isRedirecting;

  // If we're loading or in the middle of a redirect, show LoadingSpinner
  if (isLoading) {
    return <LoadingSpinner />;
  }

  const containerStyle = {
    maxWidth: '400px',
    margin: '40px auto',
    padding: theme.spacing.LARGE,
    backgroundColor: theme.colors.BACKGROUND,
    borderRadius: '8px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  };

  const inputStyle = {
    width: '100%',
    padding: theme.spacing.SMALL,
    marginBottom: theme.spacing.MEDIUM,
    border: `1px solid ${theme.colors.INPUT_BORDER}`,
    borderRadius: '4px',
    fontSize: theme.fonts.MEDIUM,
    backgroundColor: theme.colors.INPUT_BACKGROUND,
    color: theme.colors.INPUT_TEXT,
  };

  const buttonStyle = {
    width: '100%',
    padding: theme.spacing.SMALL,
    backgroundColor: theme.colors.BUTTON_PRIMARY_BG,
    color: theme.colors.BUTTON_PRIMARY_TEXT,
    border: 'none',
    borderRadius: '4px',
    fontSize: theme.fonts.MEDIUM,
    cursor: 'pointer',
    marginBottom: theme.spacing.MEDIUM,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    fontWeight: '500',
    height: '40px',
  };

  const googleButtonStyle = {
    ...buttonStyle,
    backgroundColor: theme.colors.GOOGLE_BG,
    color: theme.colors.GOOGLE_TEXT,
    border: `1px solid ${theme.colors.GOOGLE_BORDER}`,
    marginBottom: theme.spacing.LARGE,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  };

  const appleButtonStyle = {
    ...buttonStyle,
    backgroundColor: theme.colors.APPLE_BG,
    color: theme.colors.APPLE_TEXT,
    marginBottom: theme.spacing.MEDIUM,
  };

  const dividerStyle = {
    display: 'flex',
    alignItems: 'center',
    textAlign: 'center' as const,
    margin: `${theme.spacing.LARGE}px 0`,
    color: theme.colors.TEXT,
    opacity: 0.5,
  };

  const dividerLineStyle = {
    flex: 1,
    height: '1px',
    backgroundColor: 'currentColor',
  };

  const errorStyle = {
    color: theme.colors.ERROR,
    marginBottom: theme.spacing.MEDIUM,
    textAlign: 'center' as const,
  };

  const labelStyle = {
    display: 'block',
    marginBottom: theme.spacing.SMALL,
    fontWeight: '500',
  };

  return (
    <>
      <style>
        {`
          .login-input::placeholder {
            color: ${theme.colors.TEXT_SECONDARY} !important;
            opacity: 1;
          }
        `}
      </style>
      <div style={containerStyle}>

        <h2
          style={{
            textAlign: 'center',
            color: theme.colors.TEXT,
            marginBottom: theme.spacing.LARGE,
            fontSize: '1.8em',
          }}
        >
          Sign In
        </h2>

        <button
          onClick={handleGoogleSignIn}
          disabled={localLoading}
          style={googleButtonStyle}
          type="button"
        >
          <GoogleLogo />
          {localLoading ? 'Redirecting...' : 'Continue with Google'}
        </button>

        <button
          onClick={handleAppleSignIn}
          disabled={localLoading}
          style={appleButtonStyle}
          type="button"
        >
          <AppleLogo />
          {localLoading ? 'Redirecting...' : 'Continue with Apple'}
        </button>

        <div style={dividerStyle}>
          <div style={dividerLineStyle}></div>
          <span style={{ padding: '0 10px' }}>or</span>
          <div style={dividerLineStyle}></div>
        </div>

        <form onSubmit={handleSubmit}>
          <label style={labelStyle}>Email Address</label>
          <input
            type="email"
            placeholder="email@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={inputStyle}
            required
            autoComplete="username"
            autoFocus={!emailFromQuery}
          />

          <label style={labelStyle}>Password</label>
          <input
            type="password"
            placeholder="Enter your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            style={inputStyle}
            required
            autoComplete="current-password"
            ref={passwordRef}
          />

          {error && <div style={errorStyle}>{error}</div>}

          <button type="submit" disabled={localLoading} style={buttonStyle}>
            {localLoading ? 'Signing in...' : 'Sign in with email'}
          </button>
        </form>
        <div style={{
          textAlign: 'right',
          marginTop: theme.spacing.SMALL,
          marginBottom: theme.spacing.MEDIUM,
        }}>
          <Link
            to="/forgot-password"
            state={{ email: email }}
            style={{
              color: theme.colors.LINK,
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              opacity: 1,
              transition: 'all 0.2s ease',
              padding: `${theme.spacing.SMALL}px ${theme.spacing.SMALL}px`,
              borderRadius: '4px',
              backgroundColor: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.backgroundColor = theme.isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
              e.currentTarget.style.color = theme.isDark ? theme.colors.LINK : theme.colors.PRIMARY;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.backgroundColor = theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';
              e.currentTarget.style.color = theme.colors.LINK;
            }}
          >
            Forgot Password?
          </Link>
          <Link
            to="/register"
            style={{
              color: theme.colors.LINK,
              textDecoration: 'none',
              fontSize: '1rem',
              fontWeight: 500,
              opacity: 1,
              transition: 'all 0.2s ease',
              padding: `${theme.spacing.SMALL}px ${theme.spacing.SMALL}px`,
              borderRadius: '4px',
              backgroundColor: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
              display: 'inline-block',
              marginLeft: theme.spacing.SMALL,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.opacity = '0.9';
              e.currentTarget.style.backgroundColor = theme.isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)';
              e.currentTarget.style.color = theme.isDark ? theme.colors.LINK : theme.colors.PRIMARY;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.opacity = '1';
              e.currentTarget.style.backgroundColor = theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)';
              e.currentTarget.style.color = theme.colors.LINK;
            }}
          >
            Create an account
          </Link>
        </div>
      </div>
    </>
  );
};

export default Login;
